import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

export function PaymentTermsSection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [days, setDays] = useState(String(settings.payment_terms.days));
  const [label, setLabel] = useState(settings.payment_terms.label ?? "");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDays(String(settings.payment_terms.days));
    setLabel(settings.payment_terms.label ?? "");
  }, [settings]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/payment-terms", {
        days: Number.parseInt(days.trim() || "14", 10),
        label: label.trim() || null,
      });
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Zahlungskonditionen konnten nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Zahlungsziel & Zahlungsbedingungen"
      description="Standard-Zahlungsziel für ausgestellte Rechnungen. Die Fälligkeit wird beim Ausstellen automatisch aus Rechnungsdatum plus Zahlungsziel berechnet."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-4">
        <fieldset className="space-y-3" disabled={!canWrite}>
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Zahlungsziel (Tage)
            <input
              type="number"
              min="0"
              max="365"
              step="1"
              required
              className="eh-input"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              disabled={!canWrite}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Zahlungsbedingungen (optional, erscheint auf dem Rechnungs-PDF)
            <textarea
              className="eh-input min-h-[4rem] resize-y"
              rows={2}
              placeholder="z. B. Zahlbar innerhalb von 14 Tagen ohne Abzug"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={!canWrite}
            />
          </label>
        </fieldset>
        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Zahlungskonditionen gespeichert."
          error={error}
          label="Zahlungsziel speichern"
        />
      </form>
    </Card>
  );
}
